import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../services/api';
import { Affiliate, ApiResponse } from '../types';
import clsx from 'clsx';

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-green-100 text-green-800',
  suspended: 'bg-red-100 text-red-800',
  rejected: 'bg-[var(--bg-tertiary)] text-[var(--text-primary)]',
};

export default function Affiliates() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const queryClient = useQueryClient();

  const status = searchParams.get('status') || '';
  const page = parseInt(searchParams.get('page') || '1');

  const { data, isLoading } = useQuery({
    queryKey: ['affiliates', { status, search, page }],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (status) params.set('status', status);
      if (search) params.set('search', search);
      params.set('page', page.toString());

      const response = await api.get<ApiResponse<Affiliate[]>>(`/affiliates?${params}`);
      return response.data;
    },
  });

  const updateStatus = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      await api.patch(`/affiliates/${id}/status`, { status });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['affiliates'] });
      toast.success('Status updated');
    },
    onError: () => {
      toast.error('Failed to update status');
    },
  });

  const handleStatusFilter = (newStatus: string) => {
    const params = new URLSearchParams(searchParams);
    if (newStatus) {
      params.set('status', newStatus);
    } else {
      params.delete('status');
    }
    params.set('page', '1');
    setSearchParams(params);
  };

  return (
    <div>
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-[var(--text-primary)]">Affiliates</h1>
          <p className="mt-2 text-sm text-[var(--text-secondary)]">
            Manage affiliate accounts and their status.
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="mt-6 flex flex-col sm:flex-row gap-4">
        <input
          type="text"
          placeholder="Search by name, email, or code..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="input max-w-xs"
        />
        <select
          value={status}
          onChange={(e) => handleStatusFilter(e.target.value)}
          className="input max-w-xs"
        >
          <option value="">All Status</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="suspended">Suspended</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      {/* Table */}
      <div className="mt-6 overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg">
        <table className="min-w-full divide-y divide-gray-300">
          <thead className="bg-[var(--bg-secondary)]">
            <tr>
              <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-[var(--text-primary)]">
                Affiliate
              </th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-[var(--text-primary)]">
                Referral Code
              </th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-[var(--text-primary)]">
                Tenant
              </th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-[var(--text-primary)]">
                Status
              </th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-[var(--text-primary)]">
                Conversions
              </th>
              <th className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-[var(--bg-secondary)]">
            {isLoading ? (
              <tr>
                <td colSpan={6} className="py-10 text-center text-[var(--text-muted)]">
                  Loading...
                </td>
              </tr>
            ) : data?.data?.length === 0 ? (
              <tr>
                <td colSpan={6} className="py-10 text-center text-[var(--text-muted)]">
                  No affiliates found
                </td>
              </tr>
            ) : (
              data?.data?.map((affiliate) => (
                <tr key={affiliate.id}>
                  <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm">
                    <div className="font-medium text-[var(--text-primary)]">{affiliate.name}</div>
                    <div className="text-[var(--text-muted)]">{affiliate.email}</div>
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-[var(--text-muted)]">
                    <code className="bg-[var(--bg-tertiary)] px-2 py-1 rounded">
                      {affiliate.referralCode}
                    </code>
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-[var(--text-muted)]">
                    {affiliate.tenant?.name}
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm">
                    <span
                      className={clsx(
                        'badge',
                        statusColors[affiliate.status as keyof typeof statusColors]
                      )}
                    >
                      {affiliate.status}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-[var(--text-muted)]">
                    {(affiliate._count?.players || 0) + (affiliate._count?.traders || 0)}
                  </td>
                  <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                    <Link
                      to={`/affiliates/${affiliate.id}`}
                      className="text-primary-600 hover:text-primary-900 mr-4"
                    >
                      View
                    </Link>
                    {affiliate.status === 'pending' && (
                      <>
                        <button
                          onClick={() => updateStatus.mutate({ id: affiliate.id, status: 'approved' })}
                          className="text-green-600 hover:text-green-900 mr-2"
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => updateStatus.mutate({ id: affiliate.id, status: 'rejected' })}
                          className="text-red-600 hover:text-red-900"
                        >
                          Reject
                        </button>
                      </>
                    )}
                    {affiliate.status === 'approved' && (
                      <button
                        onClick={() => updateStatus.mutate({ id: affiliate.id, status: 'suspended' })}
                        className="text-red-600 hover:text-red-900"
                      >
                        Suspend
                      </button>
                    )}
                    {affiliate.status === 'suspended' && (
                      <button
                        onClick={() => updateStatus.mutate({ id: affiliate.id, status: 'approved' })}
                        className="text-green-600 hover:text-green-900"
                      >
                        Reactivate
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {data?.pagination && data.pagination.pages > 1 && (
        <div className="mt-4 flex justify-between items-center">
          <p className="text-sm text-[var(--text-muted)]">
            Showing {((page - 1) * data.pagination.limit) + 1} to{' '}
            {Math.min(page * data.pagination.limit, data.pagination.total)} of{' '}
            {data.pagination.total} results
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => {
                const params = new URLSearchParams(searchParams);
                params.set('page', (page - 1).toString());
                setSearchParams(params);
              }}
              disabled={page === 1}
              className="btn btn-secondary"
            >
              Previous
            </button>
            <button
              onClick={() => {
                const params = new URLSearchParams(searchParams);
                params.set('page', (page + 1).toString());
                setSearchParams(params);
              }}
              disabled={page >= data.pagination.pages}
              className="btn btn-secondary"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
